import { useEffect, useMemo, useState } from 'react';
import { getCharacterStats, updateCharacter, addRegular, removeRegular, blockRegular, getRegulars, ApiError, type Character, type CharacterStats, type GameTile, type MasteryRow } from '../../lib/api';
import { useEscapeClose } from '../../lib/useEscapeClose';
import { Character3DThumbnail } from './Character3DThumbnail';
import { IconPicker, type LockedCosmeticInfo } from './IconPicker';
import { SeatPreview } from './SeatPreview';
import { EmbersShop } from '../me/EmbersShop';
import { titleRarityClass } from '../me/titleRarity';
import { Coins, Swords, Award, Flame } from 'lucide-react';
import {
  DEFAULT_SEAT_COSMETICS,
  SEAT_BACKGROUNDS,
  SEAT_BG_HUES,
  SEAT_BORDERS,
  SEAT_PARTICLES,
  type SeatCosmetics,
} from '@tt/lobby';

interface Props {
  charId: string | null;
  games: GameTile[];
  editable?: boolean;
  /** The acting (logged-in) character's id — needed to call the regulars
   *  endpoints as "me" when this sheet is showing someone ELSE's profile.
   *  Unused/ignored when `editable` (viewing your own profile). */
  viewerCharId: string;
  onClose: () => void;
  onUpdated?: (character: Character) => void;
}

type TabKey = 'overview' | 'games' | 'badges';

function findGameTile(games: GameTile[], gameId: string): GameTile | undefined {
  return games.find((g) => g.id === gameId) ?? games.find((g) => g.baseGameId === gameId);
}

/**
 * CharacterProfileModal — v3 redesign (owner: "when they expand out
 * different sections, it doesnt show correctly"). The old design was an
 * accordion stack inside a whole-sheet-scrolls container: expanding a
 * section pushed the Customize button off past the viewport, clipped
 * headers mid-render on mobile, and nested a SECOND scrollbar inside each
 * section. Structural fix, not accordion patching: a fixed-height sheet
 * with a PINNED header + tab bar and exactly ONE scroll container for the
 * active tab's content (Overview / Games / Badges). This deletes the
 * entire expand-breaks-layout bug class instead of chasing each symptom.
 */
export function CharacterProfileModal({ charId, games, editable = false, viewerCharId, onClose, onUpdated }: Props) {
  const [stats, setStats] = useState<CharacterStats | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [tab, setTab] = useState<TabKey>('overview');
  const [icon, setIcon] = useState<Character['icon'] | null>(null);
  const [favoriteGameId, setFavoriteGameId] = useState<string | null>(null);
  const [selectedTitle, setSelectedTitle] = useState<string | null>(null);
  const [seatCosmetics, setSeatCosmetics] = useState<SeatCosmetics>(DEFAULT_SEAT_COSMETICS);
  const [error, setError] = useState<string | null>(null);
  useEscapeClose(charId !== null, onClose);
  const [inspect, setInspect] = useState<InspectInfo | null>(null);

  useEffect(() => {
    if (!charId) return;
    setStats(null);
    setEditing(false);
    setTab('overview');
    setError(null);
    void (async () => {
      try {
        const next = await getCharacterStats(charId);
        setStats(next);
        setIcon(next.icon);
        setFavoriteGameId(next.favoriteGameId ?? null);
        setSelectedTitle(next.selectedTitle ?? null);
        setSeatCosmetics(normalizeSeat(next.seatCosmetics));
      } catch {
        setError('Could not load profile.');
      }
    })();
  }, [charId]);

  const favorite = useMemo(() => {
    if (!stats) return null;
    const explicit = stats.favoriteGameId ? findGameTile(games, stats.favoriteGameId) : null;
    if (explicit) return explicit;
    const best = Object.entries(stats.perGame).sort((a, b) => b[1].matchesPlayed - a[1].matchesPlayed)[0];
    return best ? findGameTile(games, best[0]) ?? null : null;
  }, [games, stats]);

  if (!charId) return null;

  async function save() {
    if (!stats || !icon || busy) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await updateCharacter(stats.charId, { icon, favoriteGameId, selectedTitle, seatCosmetics });
      onUpdated?.(updated);
      const refreshed = await getCharacterStats(stats.charId);
      setStats(refreshed);
      setIcon(refreshed.icon);
      setFavoriteGameId(refreshed.favoriteGameId ?? null);
      setSelectedTitle(refreshed.selectedTitle ?? null);
      setSeatCosmetics(normalizeSeat(refreshed.seatCosmetics));
      setEditing(false);
    } catch {
      setError('Could not save profile. Some choices may still be locked.');
    } finally {
      setBusy(false);
    }
  }

  const displayTitle = selectedTitle ?? stats?.selectedTitle ?? 'Tavern Regular';

  return (
    <div className="profile-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <section className="profile-sheet" onClick={(e) => e.stopPropagation()}>
        <header className="profile-head">
          <div className="profile-identity">
            {stats && <Character3DThumbnail icon={editing && icon ? icon : stats.icon} size={116} frame="figure" circular={false} alt={stats.name} />}
            <div className="profile-identity-text">
              <h2 className="display">{stats?.name ?? 'Loading...'}</h2>
              <span className={`profile-title-plate ${titleRarityClass(displayTitle)}`}>{displayTitle}</span>
              {stats && (
                <div className="profile-level-row" aria-label="Level + season progress">
                  <span className="profile-level-chip">Lv {stats.accountLevel ?? 1}</span>
                  {stats.season && (
                    <>
                      <span className="profile-level-bar" aria-hidden="true">
                        <span style={{ width: `${Math.min(100, ((stats.season.toNext.current) / Math.max(1, stats.season.toNext.needed)) * 100)}%` }} />
                      </span>
                      <small>S{stats.season.season} · T{stats.season.tier} · {stats.season.toNext.current}/{stats.season.toNext.needed} SXP</small>
                    </>
                  )}
                </div>
              )}
              {!editable && stats && (
                <RegularsAction viewerCharId={viewerCharId} targetCharId={stats.charId} targetName={stats.name} />
              )}
            </div>
          </div>
          <button type="button" className="link" onClick={onClose}>close</button>
        </header>

        {error && <div role="alert" className="gate-error">{error}</div>}

        {!editing && stats && (
          <nav className="profile-tabs" aria-label="Profile sections">
            <button type="button" className={`profile-tab${tab === 'overview' ? ' active' : ''}`} onClick={() => setTab('overview')}>Overview</button>
            <button type="button" className={`profile-tab${tab === 'games' ? ' active' : ''}`} onClick={() => setTab('games')}>Games</button>
            <button type="button" className={`profile-tab${tab === 'badges' ? ' active' : ''}`} onClick={() => setTab('badges')}>Badges</button>
          </nav>
        )}

        <div className="profile-tab-body">
          {stats && (
            editing ? (
              <div className="profile-edit">
                <section className="profile-section profile-presence-editor">
                  <h3>Lobby presence</h3>
                  <p className="muted" style={{ margin: '0 0 0.5rem' }}>
                    This is exactly how your tile will look in the lobby. Hat, body,
                    face, hue, title flair, seat frame, backdrop, and particles all
                    render from here.
                  </p>
                  {icon && (
                    <SeatPreview
                      icon={icon}
                      seatCosmetics={seatCosmetics}
                      selectedTitle={selectedTitle ?? null}
                      name={stats.name}
                      avatarSize={64}
                    />
                  )}
                </section>
                {icon && (
                  <IconPicker
                    value={icon}
                    onChange={setIcon}
                    inventory={stats.inventory}
                    onInspectLocked={(item) => setInspect(inspectFromUnlock(item))}
                  />
                )}
                <label className="profile-field">
                  <span>Favorite game</span>
                  <select className="setting-input" value={favoriteGameId ?? ''} onChange={(e) => setFavoriteGameId(e.target.value || null)}>
                    <option value="">Auto</option>
                    {games.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                  </select>
                </label>
                <label className="profile-field">
                  <span>Title</span>
                  <select className="setting-input" value={selectedTitle ?? 'Tavern Regular'} onChange={(e) => setSelectedTitle(e.target.value)}>
                    {stats.inventory.titles.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </label>
                <PresenceEditor
                  value={seatCosmetics}
                  inventory={stats.inventory}
                  onChange={setSeatCosmetics}
                  onInspect={(item) => setInspect(inspectFromUnlock(item))}
                />
                {inspect && <InspectPanel info={inspect} onClose={() => setInspect(null)} />}
                <EmbersShop
                  charId={stats.charId}
                  stats={stats}
                  onPurchase={() => {
                    void getCharacterStats(stats.charId).then((next) => {
                      setStats(next);
                      setIcon(next.icon);
                      setSelectedTitle(next.selectedTitle ?? null);
                      setFavoriteGameId(next.favoriteGameId ?? null);
                      setSeatCosmetics(normalizeSeat(next.seatCosmetics));
                    }).catch(() => {});
                  }}
                />
                <div className="profile-actions">
                  <button type="button" className="btn ghost" onClick={() => setEditing(false)} disabled={busy}>Cancel</button>
                  <button type="button" className="btn primary" onClick={save} disabled={busy}>{busy ? 'Saving...' : 'Save'}</button>
                </div>
              </div>
            ) : tab === 'overview' ? (
              <OverviewTab stats={stats} favorite={favorite} />
            ) : tab === 'games' ? (
              <GamesTab stats={stats} games={games} />
            ) : (
              <BadgesTab stats={stats} onInspect={(item) => setInspect(inspectFromUnlock(item))} inspect={inspect} onCloseInspect={() => setInspect(null)} />
            )
          )}
        </div>

        {editable && !editing && stats && (
          <footer className="profile-footer">
            <button type="button" className="btn primary" onClick={() => setEditing(true)}>Customize</button>
          </footer>
        )}
      </section>
    </div>
  );
}

/** Maps a /regulars/add error code to human copy for the transient
 *  message under the button. Codes come straight off the D1 contract. */
function regularErrorCopy(code: string): string {
  switch (code) {
    case 'target-not-claimed': return "They haven't claimed their character yet.";
    case 'must-claim-first': return 'Set a PIN on your character first.';
    case 'blocked-by-target': return "You can't add them right now.";
    case 'unblock-first': return 'Unblock them first to re-add.';
    case 'regulars-full': return 'Your regulars list is full (50).';
    case 'target-not-found': return "Couldn't find that character.";
    case 'bad-target':
    case 'cannot-add-self':
      return "That didn't work.";
    default: return 'Could not update regulars.';
  }
}

/**
 * "Add to Regulars" action + mutual badge on another player's profile
 * sheet. Fetches the viewer's own regulars list once (cheap — capped at
 * 50 rows) to resolve this target's current state, then optimistically
 * updates on add/remove/block and reconciles against the server response.
 */
function RegularsAction({ viewerCharId, targetCharId, targetName }: { viewerCharId: string; targetCharId: string; targetName: string }) {
  const [state, setState] = useState<'loading' | 'none' | 'added' | 'mutual'>('loading');
  const [busy, setBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  useEffect(() => {
    let live = true;
    setState('loading');
    setMenuOpen(false);
    setMsg(null);
    void (async () => {
      try {
        const res = await getRegulars(viewerCharId);
        if (!live) return;
        const mine = res.regulars.find((r) => r.charId === targetCharId);
        setState(mine ? (mine.mutual ? 'mutual' : 'added') : 'none');
      } catch {
        if (live) setState('none');
      }
    })();
    return () => { live = false; };
  }, [viewerCharId, targetCharId]);

  async function add() {
    if (busy) return;
    setBusy(true);
    setMsg(null);
    setState('added'); // optimistic
    try {
      const res = await addRegular(viewerCharId, targetCharId);
      setState(res.mutual ? 'mutual' : 'added');
      setMsg({ kind: 'ok', text: 'Added ✓' });
    } catch (e) {
      setState('none');
      setMsg({ kind: 'err', text: regularErrorCopy(e instanceof ApiError ? e.code : 'unknown') });
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (busy) return;
    setBusy(true);
    setMsg(null);
    setMenuOpen(false);
    const prev = state;
    setState('none');
    try {
      await removeRegular(viewerCharId, targetCharId);
    } catch {
      setState(prev);
      setMsg({ kind: 'err', text: 'Could not remove.' });
    } finally {
      setBusy(false);
    }
  }

  async function block() {
    if (busy) return;
    setBusy(true);
    setMsg(null);
    setMenuOpen(false);
    const prev = state;
    setState('none');
    try {
      await blockRegular(viewerCharId, targetCharId);
      setMsg({ kind: 'ok', text: `Blocked ${targetName}.` });
    } catch {
      setState(prev);
      setMsg({ kind: 'err', text: 'Could not block.' });
    } finally {
      setBusy(false);
    }
  }

  if (state === 'loading') return null;

  return (
    <div className="profile-regulars-row">
      {state === 'mutual' && (
        <span className="profile-regular-badge" title="You've both added each other">☕ Regulars</span>
      )}
      {state === 'none' ? (
        <button type="button" className="btn ghost profile-regular-btn" onClick={() => void add()} disabled={busy}>
          ☕ Add to Regulars
        </button>
      ) : (
        <div className="profile-regular-menu-wrap">
          <button
            type="button"
            className="btn ghost profile-regular-btn"
            onClick={() => setMenuOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            disabled={busy}
          >
            Regular ✓ ▾
          </button>
          {menuOpen && (
            <div className="profile-regular-menu" role="menu">
              <button type="button" role="menuitem" onClick={() => void remove()}>Remove</button>
              <button type="button" role="menuitem" className="danger" onClick={() => void block()}>Block</button>
            </div>
          )}
        </div>
      )}
      {msg && <span className={`profile-regular-msg ${msg.kind}`} role="status">{msg.text}</span>}
    </div>
  );
}

/** Ledger stat chip — the 4 headline numbers every player has, front and
 *  center regardless of whether the rest of the sheet is mostly zeros. */
function StatChip({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <div className="profile-chip">
      <span className="profile-chip-icon" aria-hidden="true">{icon}</span>
      <span className="profile-chip-text">
        <strong>{value}</strong>
        <small>{label}</small>
      </span>
    </div>
  );
}

function OverviewTab({ stats, favorite }: { stats: CharacterStats; favorite: GameTile | null }) {
  const isFresh = stats.matchesPlayed === 0;
  if (isFresh) {
    return (
      <div className="profile-empty-state">
        <span aria-hidden="true" className="profile-empty-glyph">🍺</span>
        <p>New to the tavern — their story starts tonight.</p>
      </div>
    );
  }
  return (
    <div className="profile-overview">
      <div className="profile-chip-row">
        <StatChip icon={<Coins size={16} />} label="Points" value={stats.totalPoints} />
        <StatChip icon={<Swords size={16} />} label="Round wins" value={stats.roundWins} />
        <StatChip icon={<Award size={16} />} label="Set wins" value={stats.setWins} />
        <StatChip icon={<Flame size={16} />} label="Best streak" value={stats.bestDailyStreak ?? 0} />
      </div>
      <ul className="profile-ledger">
        <li><span>Rounds played</span><strong>{stats.matchesPlayed}</strong></li>
        <li><span>Awards</span><strong>{stats.mvpAwards}</strong></li>
        <li><span>Daily streak</span><strong>{stats.dailyStreak ?? 0}{(stats.dailyStreak ?? 0) > 0 ? ' 🔥' : ''}</strong></li>
        <li><span>Favorite game</span><strong>{favorite?.name ?? 'None yet'}</strong></li>
      </ul>
    </div>
  );
}

interface GameRow {
  id: string;
  name: string;
  matchesPlayed: number;
  roundWins: number;
  bestScore: number;
  masteryTier: 0 | 1 | 2 | 3 | null;
  masteryNext: { label: string; hint: string } | null;
}

function buildGameRows(stats: CharacterStats, games: GameTile[]): GameRow[] {
  const masteryRows: MasteryRow[] = stats.inventory.mastery ?? [];
  const masteryById = new Map(masteryRows.map((m) => [m.gameId, m]));
  const idToName = new Map<string, string>();
  for (const m of masteryRows) idToName.set(m.gameId, m.gameName);
  for (const g of games) idToName.set(g.id, g.name);
  for (const id of Object.keys(stats.perGame)) if (!idToName.has(id)) idToName.set(id, id);
  return Array.from(idToName.entries()).map(([id, name]) => {
    const row = stats.perGame[id];
    const m = masteryById.get(id);
    return {
      id,
      name,
      matchesPlayed: row?.matchesPlayed ?? 0,
      roundWins: row?.roundWins ?? row?.wins ?? 0,
      bestScore: row?.bestScore ?? 0,
      masteryTier: m ? m.tier : null,
      masteryNext: m?.nextHint ? { label: m.nextLabel ?? '', hint: m.nextHint } : null,
    };
  }).sort((a, b) => b.matchesPlayed - a.matchesPlayed || a.name.localeCompare(b.name));
}

function masteryPips(tier: 0 | 1 | 2 | 3 | null): string {
  if (tier === null) return '';
  return tier === 0 ? '○○○' : tier === 1 ? '●○○' : tier === 2 ? '●●○' : '●●●';
}

function GamesTab({ stats, games }: { stats: CharacterStats; games: GameTile[] }) {
  const [showUnplayed, setShowUnplayed] = useState(false);
  const rows = useMemo(() => buildGameRows(stats, games), [stats, games]);
  const played = rows.filter((r) => r.matchesPlayed > 0);
  const unplayed = rows.filter((r) => r.matchesPlayed === 0);

  if (rows.length === 0) return <p className="muted">No games loaded yet.</p>;

  return (
    <div className="profile-game-list">
      {played.length === 0 && (
        <div className="profile-empty-state profile-empty-state-compact">
          <p>Hasn't picked a table yet — {rows.length} games waiting.</p>
        </div>
      )}
      {played.map((row) => (
        <div key={row.id} className="profile-game-row">
          <strong>{row.name}</strong>
          <span>{row.matchesPlayed} played</span>
          <span>{row.roundWins} wins</span>
          <span>best {row.bestScore}</span>
          {row.masteryTier !== null && (
            <span className="profile-game-mastery" title={row.masteryNext ? `${row.masteryNext.label}: ${row.masteryNext.hint}` : 'Mastered'} aria-label={`mastery tier ${row.masteryTier}`}>
              {masteryPips(row.masteryTier)}
            </span>
          )}
        </div>
      ))}
      {unplayed.length > 0 && (
        <>
          <button type="button" className="profile-unplayed-toggle link" onClick={() => setShowUnplayed((v) => !v)}>
            {showUnplayed ? 'Hide untried games' : `+${unplayed.length} games not yet tried`}
          </button>
          {showUnplayed && unplayed.map((row) => (
            <div key={row.id} className="profile-game-row unplayed">
              <strong>{row.name}</strong>
              <span>0 played</span>
              <span>0 wins</span>
              <span>best —</span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function BadgesTab({
  stats, onInspect, inspect, onCloseInspect,
}: {
  stats: CharacterStats;
  onInspect: (item: LockedCosmeticInfo) => void;
  inspect: InspectInfo | null;
  onCloseInspect: () => void;
}) {
  const titleHints = (stats.inventory.unlockHints ?? []).filter((h) => h.kind === 'title');
  const ownedTitles = new Set(stats.inventory.titles);
  const equipped = stats.selectedTitle ?? 'Tavern Regular';
  return (
    <div className="profile-badges">
      <section className="profile-section">
        <h3>Badges</h3>
        <div className="profile-badge-row">
          {stats.achievements.map((a) => <span key={a.code} className="award-chip">{a.label}</span>)}
          {stats.achievements.length === 0 && (
            <div className="profile-empty-state profile-empty-state-compact">
              <p>No badges yet — every table win, streak, and milestone earns one.</p>
            </div>
          )}
        </div>
      </section>
      <section className="profile-section">
        <h3>Titles</h3>
        <div className="profile-title-list">
          <span className={`unlock-chip unlocked profile-title-equipped`}>★ {equipped} (equipped)</span>
          {[...new Set(['Tavern Regular', ...stats.inventory.titles])]
            .filter((t) => t !== equipped)
            .map((t) => (
              <span key={t} className="unlock-chip unlocked">{t}</span>
            ))}
          {titleHints.filter((h) => !h.unlocked && !ownedTitles.has(String(h.id))).map((h) => (
            <button
              key={`title-${h.id}`}
              type="button"
              className="unlock-chip"
              title={h.hint}
              onClick={() => onInspect({ kind: 'title', id: h.id, label: h.label, hint: h.hint, ...(h.progress ? { progress: h.progress } : {}) })}
            >
              {formatUnlockHint(h)}
            </button>
          ))}
        </div>
      </section>
      {inspect && <InspectPanel info={inspect} onClose={onCloseInspect} />}
    </div>
  );
}

interface InspectInfo {
  kind: string;
  title: string;
  body: string;
  action: string;
}

function InspectPanel({ info, onClose }: { info: InspectInfo; onClose: () => void }) {
  return (
    <div className="profile-inspect-panel" aria-live="polite">
      <div>
        <span>{info.kind}</span>
        <button type="button" className="link" onClick={onClose}>close</button>
      </div>
      <strong>{info.title}</strong>
      <p>{info.body}</p>
      <small>{info.action}</small>
    </div>
  );
}

function PresenceEditor({
  value,
  inventory,
  onChange,
  onInspect,
}: {
  value: SeatCosmetics;
  inventory: CharacterStats['inventory'];
  onChange: (next: SeatCosmetics) => void;
  onInspect: (item: LockedCosmeticInfo) => void;
}) {
  return (
    <section className="profile-section presence-locked-section">
      <h3>Seat cosmetics</h3>
      <PresenceGrid
        title="Borders"
        kind="seatBorder"
        selected={value.border}
        items={SEAT_BORDERS}
        unlocked={inventory.seatBorders ?? ['none']}
        onSelect={(border) => onChange({ ...value, border })}
        onInspect={onInspect}
        hints={inventory.unlockHints ?? []}
      />
      <PresenceGrid
        title="Backgrounds"
        kind="seatBackground"
        selected={value.background}
        items={SEAT_BACKGROUNDS}
        unlocked={inventory.seatBackgrounds ?? ['plain', 'tavern']}
        onSelect={(background) => onChange({ ...value, background })}
        onInspect={onInspect}
        hints={inventory.unlockHints ?? []}
      />
      <PresenceGrid
        title="Particles"
        kind="seatParticle"
        selected={value.particle}
        items={SEAT_PARTICLES}
        unlocked={inventory.seatParticles ?? ['none']}
        onSelect={(particle) => onChange({ ...value, particle })}
        onInspect={onInspect}
        hints={inventory.unlockHints ?? []}
      />
      <SeatBgHueGrid
        selected={value.bgHue ?? null}
        unlocked={inventory.seatBgHues ?? [25, 210, 290]}
        hints={inventory.unlockHints ?? []}
        onSelect={(bgHue) => onChange({ ...value, bgHue })}
        onInspect={onInspect}
      />
    </section>
  );
}

function SeatBgHueGrid({
  selected,
  unlocked,
  hints,
  onSelect,
  onInspect,
}: {
  selected: number | null;
  unlocked: number[];
  hints: NonNullable<CharacterStats['inventory']['unlockHints']>;
  onSelect: (next: number | null) => void;
  onInspect: (item: LockedCosmeticInfo) => void;
}) {
  return (
    <div className="presence-category">
      <h4 className="unlock-group-title">Background hue</h4>
      <p className="muted" style={{ margin: '0 0 0.4rem', fontSize: '0.78rem' }}>
        Pick a custom gradient color for your tile. "Off" falls back to the
        themed background you picked above.
      </p>
      <div className="hue-row">
        <button
          type="button"
          className={`hue-cell hue-cell-off${selected === null ? ' selected' : ''}`}
          onClick={() => onSelect(null)}
          aria-label="No custom hue (use preset background)"
          title="Off — use the themed background above"
        >
          <span style={{ fontSize: '0.7rem' }}>off</span>
        </button>
        {SEAT_BG_HUES.map((h) => {
          const isUnlocked = unlocked.includes(h.id);
          const hint = hints.find((x) => x.kind === 'seatBgHue' && Number(x.id) === h.id);
          return (
            <button
              key={h.id}
              type="button"
              className={`hue-cell${selected === h.id ? ' selected' : ''}${isUnlocked ? '' : ' locked'}`}
              onClick={() => isUnlocked
                ? onSelect(h.id)
                : onInspect(hint ?? { kind: 'seatBgHue', id: h.id, label: h.label, hint: 'Unlock through play.' })}
              style={{ background: `hsl(${h.id} 70% 50%)` }}
              aria-label={isUnlocked ? `${h.label} (hue ${h.id})` : `${h.label} locked`}
              title={isUnlocked ? h.label : `${h.label} locked`}
            />
          );
        })}
      </div>
    </div>
  );
}

function PresenceGrid<T extends string>({
  title,
  kind,
  selected,
  items,
  unlocked,
  onSelect,
  onInspect,
  hints,
}: {
  title: string;
  kind: LockedCosmeticInfo['kind'];
  selected: T;
  items: Array<{ id: T; label: string; description: string }>;
  unlocked: string[];
  onSelect: (id: T) => void;
  onInspect: (item: LockedCosmeticInfo) => void;
  hints: NonNullable<CharacterStats['inventory']['unlockHints']>;
}) {
  return (
    <div className="presence-category">
      <h4 className="unlock-group-title">{title}</h4>
      <div className="presence-locked-grid">
        {items.map((item) => {
          const isUnlocked = unlocked.includes(item.id);
          const hint = hints.find((h) => h.kind === kind && h.id === item.id);
          return (
            <button
              key={item.id}
              type="button"
              className={`presence-locked-card presence-tile-${item.id}${selected === item.id ? ' selected' : ''}${isUnlocked ? '' : ' locked'}`}
              onClick={() => isUnlocked ? onSelect(item.id) : onInspect(hint ?? { kind, id: item.id, label: item.label, hint: 'Unlock this through play.' })}
              title={isUnlocked ? item.description : `${item.label} locked - click to see how to unlock`}
            >
              <strong>{item.label}</strong>
              <span>{isUnlocked ? (selected === item.id ? 'Equipped' : 'Unlocked') : formatUnlockHint(hint ?? { hint: 'Locked.' })}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function inspectFromUnlock(item: LockedCosmeticInfo): InspectInfo {
  const info = rewardKindInfo(item.kind);
  return {
    kind: info.kind,
    title: item.label,
    body: info.body,
    action: `How to unlock: ${formatUnlockHint(item)}`,
  };
}

function rewardKindInfo(kind: LockedCosmeticInfo['kind']): { kind: string; body: string } {
  switch (kind) {
    case 'body':
      return { kind: 'Character body', body: 'A new avatar body shape. Equip it in Customize once unlocked.' };
    case 'hat':
      return { kind: 'Hat cosmetic', body: 'A head accessory for your avatar. Other players see it on your character.' };
    case 'face':
      return { kind: 'Face cosmetic', body: 'A new avatar expression. It changes your look, not gameplay.' };
    case 'hue':
      return { kind: 'Body color', body: 'A main avatar color. Use it to change your character palette.' };
    case 'accentHue':
      return { kind: 'Accent color', body: 'A secondary color for hats, blush, and small character details.' };
    case 'title':
      return { kind: 'Title', body: 'A selectable nameplate title. It appears on your profile and under your name in the lobby.' };
    case 'emote':
      return { kind: 'Reaction emote', body: 'An extra reaction for the in-game reaction picker.' };
    case 'seatBorder':
      return { kind: 'Seat border', body: 'An animated frame around your lobby player tile. Other players see it in the room.' };
    case 'seatBackground':
      return { kind: 'Seat background', body: 'A backdrop behind your avatar on the lobby player tile.' };
    case 'seatParticle':
      return { kind: 'Seat particles', body: 'Small ambient effects around your lobby player tile.' };
    case 'seatBgHue':
      return { kind: 'Seat background hue', body: 'A custom gradient color for your lobby tile background. Overrides the themed backdrop above.' };
    default:
      return { kind: 'Reward', body: 'A reward for your character or account presence.' };
  }
}

function formatUnlockHint(hint: { hint: string; progress?: { current: number; target: number; label: string } }): string {
  if (!hint.progress) return hint.hint;
  const current = Math.min(hint.progress.current, hint.progress.target);
  return `${hint.hint} ${current}/${hint.progress.target} ${hint.progress.label}.`;
}

function normalizeSeat(value: CharacterStats['seatCosmetics']): SeatCosmetics {
  return {
    border: (value?.border as SeatCosmetics['border']) ?? DEFAULT_SEAT_COSMETICS.border,
    background: (value?.background as SeatCosmetics['background']) ?? DEFAULT_SEAT_COSMETICS.background,
    particle: (value?.particle as SeatCosmetics['particle']) ?? DEFAULT_SEAT_COSMETICS.particle,
    bgHue: (value as { bgHue?: number | null } | undefined)?.bgHue ?? null,
  };
}
